<template>
  <slot v-bind="{ errors: validationErrors, rootErrors }" />
</template>

<script>
import { computed } from "vue";
import { getScrollParent } from "~/utils/DOM";

export default {
  name: "BaseValidationErrorsBoundary",

  provide() {
    return {
      errors: this.validationErrors,
      rootErrors: computed(() => this.rootErrors),
    };
  },

  props: {
    errors: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      accessedErrorFields: new Set(),
      validationErrors: this.createErrorProxy(this.errors),
    };
  },

  computed: {
    rootErrors() {
      const result = {};

      Object.entries(this.errors).forEach(([key, value]) => {
        if (this.accessedErrorFields.has(key) || key === "__humanNames") {
          return;
        }

        if (!value || value.length < 1) {
          return;
        }

        const name = this.errors.__humanNames?.[key] || key;

        if (Object.hasOwn(result, name)) {
          result[name].push(...value);
        } else {
          result[name] = [...value];
        }
      });

      return result;
    },
  },

  watch: {
    errors: {
      deep: true,
      handler(newErrors) {
        this.validationErrors = this.createErrorProxy(newErrors);
        if (Object.keys(newErrors).length > 0) {
          this.$nextTick(() => {
            const target = this.$el.parentElement.querySelector("[aria-invalid=true], #form-root-errors");

            if (!target) return;

            // Don't yank the scroll position toward a field the user is actively
            // editing — as-you-type validation (e.g. the Kaldenavn name error)
            // would otherwise scroll the field out from under the cursor. Submit
            // errors still scroll, because focus is then on the submit button.
            if (target === document.activeElement || target.contains(document.activeElement)) return;

            const scrollParent = getScrollParent(target.parentElement);
            const inputTop = target.getBoundingClientRect().top;
            const scrollParentTop = scrollParent.getBoundingClientRect().top;
            const relativeTop = inputTop - scrollParentTop;
            const labelOffset = 32;
            const top = scrollParent.scrollTop + relativeTop - labelOffset;

            scrollParent.scrollTo({ top, behavior: "smooth" });
          });
        }
      },
    },
  },

  methods: {
    createErrorProxy(errorsObj) {
      const self = this;

      return new Proxy(errorsObj, {
        get(target, property, receiver) {
          if (typeof property === "string" && property !== "then" && !property.startsWith("__")) {
            self.accessedErrorFields.add(property);
          }

          return Reflect.get(target, property, receiver);
        },
      });
    },

    getAccessedErrorFields() {
      return Array.from(this.accessedErrorFields);
    },

    clearAccessedErrorFields() {
      this.accessedErrorFields.clear();
    },
  },
};
</script>
